import React, { useCallback, useState } from 'react';
import { Provider } from 'react-redux';

import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { ApiClient } from '@provider-portal/common/api';
import FullViewSpinner from '@provider-portal/common/components/FullViewSpinner/FullViewSpinner';
import config from '@provider-portal/common/config';
import {
  ProviderPortalModuleConfig,
  ProviderPortalModuleSetup,
} from '@provider-portal/common/Module';
import { AccountService } from '@provider-portal/common/services/AccountService';
import { CommonApplicationServices } from '@provider-portal/common/services/CommonApplicationServices';
import { EventAliasingService } from '@provider-portal/common/services/EventAliasingService';
import { MixpanelService } from '@provider-portal/common/services/MixpanelService';
import { NotificationService } from '@provider-portal/common/services/NotificationService';
import { ProviderPortalNavigationService } from '@provider-portal/common/services/ProviderPortalNavigationService';
import { TrackingService } from '@provider-portal/common/services/TrackingService';
import { theme } from '@provider-portal/common/styles/themeMui';
import { HttpClientBoltServerConstraints } from '@provider-portal/common/types/types';
import { PersistGate } from 'redux-persist/integration/react';

import { HttpClientBoltServer } from '@bolteu/http-client-bolt-server';
import { App, Facade, ModuleConfig, RenderProps } from '@bolteu/lattice';
import { waitForAccessTokenUpdate } from '@bolteu/redux-session';

import { createGatewayHooks } from 'api/hooks';
import { createGatewaySelectors } from 'api/selectors';
import { AppServices } from 'rdx/middleware';
import { createCredentialsSessionEpicDependencies } from 'rdx/session/createCredentialsSessionEpicDependencies';
import { createReduxStore } from 'rdx/store';
import { ApplicationUpdateService } from 'services/ApplicationUpdateService';
import { createBugsnagStuff } from 'services/bugsnag';
import { initializeGTMSnippet } from 'utils/gtagSnippet';

import { AppServicesContext } from 'components/AppServicesProvider/AppServicesProvider';

import { SessionService } from '../../common/all/services/SessionService';
import { TargetingService } from '../../common/all/services/TargetingService';
import { createObservableAndStateFromGlobalStore } from './adapters';
import { commonAccountAdapter } from './adapters/CommonAccountAdapter';
import { urlLocationAdapter } from './adapters/UrlLocationAdapter';
import { AppContainer } from './AppContainer';
import { ThemeListener } from './ThemeListener';

import '@provider-portal/common/styles/index.css';
import 'react-perfect-scrollbar/dist/css/styles.css';

const { bugsnag, ErrorBoundary } = createBugsnagStuff(config);

const FastClick = require('fastclick');

if (FastClick.attach !== undefined) {
  FastClick.attach(document.body, {});
} else {
  FastClick(document.body, {});
}

const initAppProps = (
  httpClientBoltServer: HttpClientBoltServer<HttpClientBoltServerConstraints>,
) => {
  const apiClient = new ApiClient(httpClientBoltServer);
  const reduxSession = createCredentialsSessionEpicDependencies(apiClient, bugsnag);

  const mixpanelService = new MixpanelService();
  const eventAliasingService = new EventAliasingService(mixpanelService);
  const applicationUpdateService = new ApplicationUpdateService();

  const appServices: AppServices = {
    mixpanelService,
    eventAliasingService,
    config,
    bugsnag,
    reduxSession,
    apiClient,
    applicationUpdateService,
  };
  const { store, persistor, history } = createReduxStore(appServices);

  const gatewaySelectors = createGatewaySelectors({ store, config });

  const accountChangeObservable = createObservableAndStateFromGlobalStore(
    store,
    commonAccountAdapter,
    true,
  );
  const locationChangeObservable = createObservableAndStateFromGlobalStore(
    store,
    urlLocationAdapter,
    false,
  );

  const accountService = new AccountService(accountChangeObservable);
  const trackingService = new TrackingService(accountService);
  const sessionService = new SessionService(store, apiClient);

  const commonAppServices = new CommonApplicationServices(
    accountService,
    appServices.apiClient,
    appServices.bugsnag,
    appServices.mixpanelService,
    trackingService,
    appServices.eventAliasingService,
    new NotificationService(store),
    new TargetingService(store),
    sessionService,
    config,
  );

  return {
    store,
    persistor,
    history,
    commonAppServices,
    locationChangeObservable,
    appServices,
    gatewaySelectors,
    applicationUpdateService,
  };
};

const httpClientBoltServer = new HttpClientBoltServer<HttpClientBoltServerConstraints>({
  baseUrl: config.apiBaseUrl,
});
const appProps = initAppProps(httpClientBoltServer);

export interface ProviderPortalAppService {
  publisher: Facade.Publisher;
  navigationService: Facade.GlobalNavigationService;
  moduleManifests: ProviderPortalModuleManifest[];
}

export interface ProviderPortalModuleManifest extends Facade.ModuleManifest {
  isPublic: boolean;
}

interface AppEntryProps {
  moduleManifests: ProviderPortalModuleManifest[];
  navigationService: ProviderPortalNavigationService;
}

const AppEntry: React.FC<AppEntryProps> = (props) => {
  const [isReady, setReadyState] = useState(false);

  const initAppDeps = useCallback(async () => {
    try {
      // init API
      appProps.appServices.apiClient.init({
        hooks: createGatewayHooks({ store: appProps.store }),
        selectors: appProps.gatewaySelectors,
      });
    } catch (_e) {
      // TODO
    }
    setReadyState(true);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <AppServicesContext.Provider value={appProps.appServices}>
        <ErrorBoundary>
          <Provider store={appProps.store}>
            <ThemeListener />
            <PersistGate loading={null} persistor={appProps.persistor} onBeforeLift={initAppDeps}>
              {isReady ? (
                <AppContainer
                  moduleManifests={props.moduleManifests}
                  history={appProps.history}
                  navigationService={props.navigationService}
                  commonAppServices={appProps.commonAppServices}
                >
                  {props.children}
                </AppContainer>
              ) : (
                <FullViewSpinner size={theme.spacing(6)} />
              )}
            </PersistGate>
          </Provider>
        </ErrorBoundary>
      </AppServicesContext.Provider>
    </ThemeProvider>
  );
};

export class ProviderPortalApp extends App {
  private services!: ProviderPortalAppService;
  private providerPortalNavService!: ProviderPortalNavigationService;
  protected httpClientBoltServer!: HttpClientBoltServer<HttpClientBoltServerConstraints>;

  onModuleLoaded(
    moduleConstructor: new (options: ProviderPortalModuleConfig) => ProviderPortalModuleSetup,
    moduleConfig: ModuleConfig,
  ) {
    const ModuleClassConstructor = moduleConstructor;

    const module = new ModuleClassConstructor({
      ...moduleConfig,
      httpClientBoltServer: this.httpClientBoltServer,
      latticeNavigationService: this.services.navigationService,
      navigationService: this.providerPortalNavService,
      publisher: this.services.publisher,
      commonAppServices: appProps.commonAppServices,
    });

    this.services.publisher.publish({
      type: 'module-init',
      payload: {
        module,
      },
    });
  }

  public init(providerPortalAppService: ProviderPortalAppService): void {
    this.services = providerPortalAppService;

    this.httpClientBoltServer = httpClientBoltServer;

    this.providerPortalNavService = new ProviderPortalNavigationService(
      this.services.navigationService,
      appProps.locationChangeObservable,
    );
    if (process.env.REACT_APP_STAGE === 'live') {
      initializeGTMSnippet({
        tagId: 'GTM-WGNQRNT',
      });
    }

    this.httpClientBoltServer.init({
      authStrategy: {
        jwt: {
          getAuthToken: appProps.gatewaySelectors.getAccessToken,
          getExtraConfig() {
            return {
              updateAuthToken: (): Promise<string> => {
                return waitForAccessTokenUpdate(appProps.store);
              },
              accessTokenBlockingTimeoutBeforeExpiryInSeconds:
                config.accessTokenBlockingTimeoutBeforeExpiryInSeconds,
              accessTokenRefreshTimeoutBeforeExpiryInSeconds:
                config.accessTokenRefreshTimeoutBeforeExpiryInSeconds,
            };
          },
        },
      },
      standardRequestStrategy: {
        getStandardRequest: appProps.gatewaySelectors.getDefaultQueryParams,
      },
    });

    super.init(providerPortalAppService);
  }

  render(props: RenderProps) {
    return (
      <StyledEngineProvider injectFirst>
        <AppEntry
          moduleManifests={this.services.moduleManifests}
          navigationService={this.providerPortalNavService}
        >
          {props.children}
        </AppEntry>
      </StyledEngineProvider>
    );
  }

  /*
   * Lattice doesn't support provider-portal like Crowdin based translations at the moment.
   * PRs welcomed! :)
   */
  getTranslations() {
    return {};
  }
}
